import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield, Users, AlertTriangle, LogIn, Eye, Search, RefreshCw, Clock, User } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import LiveActivityFeed from '../../components/LiveActivityFeed';
import ActivityHeatmap from '../../components/ActivityHeatmap';

export default function AdminSecurityDashboard() {
  const { axiosAuth } = useAuth();
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', severity: '', days: '7' });
  const [searchUserId, setSearchUserId] = useState('');
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, logsRes] = await Promise.all([
        api.get(`/audit/summary?days=${filters.days}`),
        api.get(`/audit/logs?days=${filters.days}&limit=100${filters.action ? `&action=${filters.action}` : ''}${filters.severity ? `&severity=${filters.severity}` : ''}`)
      ]);
      setSummary(summaryRes.data);
      setLogs(logsRes.data);
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [filters.days]);

  const handleSearch = () => {
    fetchData();
  };

  const handleUserSearch = async () => {
    if (!searchUserId.trim()) return;
    try {
      const res = await api.get(`/audit/user-activity/${searchUserId}?days=30`);
      setLogs(res.data.activity || []);
    } catch (err) {
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <LogIn size={14} className="text-emerald-600" />;
      case 'access_denied': return <AlertTriangle size={14} className="text-red-600" />;
      default: return <Eye size={14} className="text-sky-600" />;
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-security-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Security Dashboard
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Monitor user activity, logins, and security events
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2 rounded-full" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-0 shadow-sm rounded-2xl bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-teal-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.total_events}</p>
              <p className="text-xs text-muted-foreground">Total Events</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <LogIn className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.total_logins}</p>
              <p className="text-xs text-muted-foreground">Logins</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                  <Users className="w-4 h-4 text-sky-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.active_users}</p>
              <p className="text-xs text-muted-foreground">Active Users</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.access_denials}</p>
              <p className="text-xs text-muted-foreground">Access Denials</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Eye className="w-4 h-4 text-amber-600" />
                </div>
              </div>
              <p className="text-2xl font-bold">{summary.sensitive_actions}</p>
              <p className="text-xs text-muted-foreground">Sensitive Actions</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Live Activity (real-time middleware-captured logs) */}
      <LiveActivityFeed />

      {/* Activity Heat-Map (hour × day) */}
      <ActivityHeatmap />

      {/* Activity by Role */}
      {summary?.by_role && Object.keys(summary.by_role).length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Activity by Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.by_role).map(([role, count]) => (
                <Badge key={role} variant="outline" className="px-3 py-1.5 text-sm">
                  {role}: <span className="font-bold ml-1">{count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <Select value={filters.days} onValueChange={(v) => setFilters({ ...filters, days: v })}>
              <SelectTrigger className="w-full md:w-40 rounded-xl">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.action || 'all'} onValueChange={(v) => setFilters({ ...filters, action: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-full md:w-40 rounded-xl">
                <SelectValue placeholder="Action Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="access_denied">Access Denied</SelectItem>
                <SelectItem value="connection_request">Connection Request</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity || 'all'} onValueChange={(v) => setFilters({ ...filters, severity: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-full md:w-40 rounded-xl">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2">
              <Search size={16} /> Search
            </Button>
            <div className="flex gap-2 flex-1">
              <Input 
                placeholder="Search by User ID" 
                value={searchUserId}
                onChange={(e) => setSearchUserId(e.target.value)}
                className="rounded-xl"
              />
              <Button onClick={handleUserSearch} variant="outline" className="rounded-full gap-2">
                <User size={16} /> User
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Sensitive Actions */}
      {summary?.recent_sensitive?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <AlertTriangle className="text-amber-500" size={18} /> Recent Sensitive Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.recent_sensitive.map((log, idx) => (
                <div key={`item-${idx}`} className="flex items-center gap-3 p-2.5 bg-amber-50/50 rounded-xl text-sm">
                  <span className="font-medium">{log.user_name}</span>
                  <Badge className={getSeverityColor(log.severity)}>{log.action}</Badge>
                  <span className="text-muted-foreground flex-1 truncate">{log.details}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Log Table */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Activity Log</CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No audit logs found for the selected filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">Time</th>
                    <th className="text-left py-2 px-3 font-medium">User</th>
                    <th className="text-left py-2 px-3 font-medium">Role</th>
                    <th className="text-left py-2 px-3 font-medium">Action</th>
                    <th className="text-left py-2 px-3 font-medium">Details</th>
                    <th className="text-left py-2 px-3 font-medium">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={`item-${idx}`} className="border-b hover:bg-muted/30">
                      <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock size={12} />
                          {formatDate(log.timestamp)}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 font-medium">{log.user_name}</td>
                      <td className="py-2.5 px-3">
                        <Badge variant="outline" className="text-xs">{log.user_role}</Badge>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          {getActionIcon(log.action)}
                          {log.action}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-muted-foreground max-w-xs truncate">{log.details}</td>
                      <td className="py-2.5 px-3">
                        <Badge className={getSeverityColor(log.severity)}>{log.severity}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
